// src/sections/privacy/ZKPABootstrapModal.tsx
// Shown when ZKPA not yet configured for this user.
// BLOCKS all app access until setup is complete — cannot be dismissed.

import { Button, Text } from "@opal/components";
import { SvgShieldCheck } from "@opal/icons";

interface Props {
  open: boolean;
  /** Opens SeedPhraseSetupModal */
  onStartSetup: () => void;
  /** Optional callback to dismiss modal */
  onDismiss?: () => void;
}

export function ZKPABootstrapModal({ open, onStartSetup, onDismiss }: Props) {
  if (!open) return null;

  return (
    // Blocking overlay — cannot be dismissed
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      id="zkpa-bootstrap-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="zkpa-bootstrap-title"
    >
      <div className="bg-[var(--surface-01)] rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center gap-2 mb-4">
          <SvgShieldCheck
            className="h-6 w-6 text-[var(--color-primary)]"
            aria-hidden="true"
          />
          <Text size="lg" weight="semibold" as="h2" id="zkpa-bootstrap-title">
            Thiết lập bảo mật dữ liệu
          </Text>
        </div>

        <div className="space-y-4">
          <Text size="sm" color="subtle">
            VNP Workplace bảo vệ dữ liệu của bạn bằng{" "}
            <strong>mã hoá đầu cuối</strong>. Bạn cần tạo{" "}
            <strong>cụm từ khôi phục (seed phrase)</strong> — đây là chìa khóa
            duy nhất để truy cập dữ liệu của bạn.
          </Text>

          <div
            className="bg-[var(--status-warning-00)] border border-[var(--status-warning-02)] rounded-md p-3"
            role="note"
          >
            <Text size="sm">
              ⚠️ <strong>Quan trọng:</strong> Không ai — kể cả quản trị viên VNP
              — có thể khôi phục dữ liệu của bạn nếu mất cụm từ này. Hãy lưu
              cẩn thận.
            </Text>
          </div>

          <ul className="space-y-1 text-sm">
            <li className="flex items-center gap-2">
              <span className="text-[var(--color-primary)]">✓</span>
              Chỉ bạn mới có thể đọc dữ liệu của mình
            </li>
            <li className="flex items-center gap-2">
              <span className="text-[var(--color-primary)]">✓</span>
              Server chỉ lưu dữ liệu đã mã hoá
            </li>
            <li className="flex items-center gap-2">
              <span className="text-[var(--color-primary)]">✓</span>
              Chia sẻ an toàn P2P — không lộ token kết nối
            </li>
          </ul>

          <div className="flex gap-2">
            {onDismiss && (
              <Button
                id="zkpa-bootstrap-dismiss-btn"
                data-testid="zkpa-bootstrap-dismiss"
                variant="secondary"
                className="w-1/3"
                onClick={onDismiss}
              >
                Bỏ qua
              </Button>
            )}
            <Button
              id="zkpa-start-setup-btn"
              variant="primary"
              className={onDismiss ? "w-2/3" : "w-full"}
              onClick={onStartSetup}
            >
              Bắt đầu thiết lập (~ 2 phút)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
